import { inject, Injectable } from '@angular/core';
import { CookieService } from 'ngx-unificator/services';
import { of, Subject } from 'rxjs';
import { catchError, first, map, takeUntil, tap } from 'rxjs/operators';
import { CmsApiService } from '../services/api/cms-api.service';
import { SsApiService } from '../services/api/ss-api.service';
import { GoogleTagManagerService } from '../services/google-tag-manager.service';
import { UserService } from '../services/user/user.service';
import { AB_TEST_LIST, ABTestTaskListType, ABVariantsType } from './ab-test.data';
import { createABTestDebugMessage } from './ab-test-debug';
import { LocalstorageService } from '../services/localstorage.service';
import { isDesktop, isTablet } from '../helpers/device';

@Injectable({
  providedIn: 'root',
})
export class AbTestNewService {

  private _api = inject(SsApiService);
  private _cmsApi = inject(CmsApiService);
  private _cookie = inject(CookieService);
  private _gtm = inject(GoogleTagManagerService);
  private _user = inject(UserService);
  private _storage = inject(LocalstorageService);

  public popularPaymentsDEVSS5667 = [];

  /**
   * The function `loadABTest$` loads an AB test value from a CMS API and handles caching and error
   * handling.
   * @param {ABTestTaskListType} abTestTask - The `abTestTask` parameter in the `loadABTest$` function
   * represents the type of AB test task that needs to be loaded. It is used to determine which AB test
   * configuration to fetch and apply.
   * @returns The `loadABTest$` function returns an Observable that emits the resolved value of an AB
   * test.
   */
  public loadABTest$(abTestTask: ABTestTaskListType) {
    const abTest = AB_TEST_LIST[abTestTask];

    if (abTest?.resolvedValue) {
      abTest.resolvedValue$?.next(abTest.resolvedValue);
      return abTest.resolvedValue$;
    }

    const _headers = this._createHeaders(abTest?.headerMainGroup);
    this._cmsApi.loadABTest(null, { headers: _headers }).pipe(
      map((e: { data: any[] }) => e.data?.[0]?.slug ?? null),
      tap((abTestValue: ABVariantsType) => this._handleResolvedValue(abTest, abTestTask, abTestValue)),
      catchError(() => {
        abTest.resolvedValue$.next(null);
        return of(null);
      }),
      first(),
    ).subscribe();

    return abTest.resolvedValue$;
  }

  /**
   * Creates headers for the AB test request.
   * @param headerMainGroup - The main group header.
   * @returns The headers object.
   */
  private _createHeaders(headerMainGroup: string) {
    return {
      'Content-Type': 'application/json',
      'u-group': headerMainGroup,
      'DEVICE-TYPE': isDesktop() ? 'Desktop' : isTablet() ? 'Tablet' : 'Mobile',
    };
  }

  /**
   * Add user to AB test group after registration
   */
  private _addUserToABTestGroup(prefix: ABVariantsType) {
    this._api.postPlayerGroups({ groups: { add: [prefix] } }).pipe(
      first(),
      catchError(error => of(error)),
    ).subscribe(() => {
      this._user.info.statuses.push({ name: '', id: prefix });
    });
  }

  /**
   * Check if user has one of A or B test group, by ab test list
   * @param userStatusList
   * @param abTestIdList
   */
  public checkExistUserGroups(abTestIdList: any[]): string | null {
    const userStatuses = this._user?.info?.statuses ?? [];
    const matchingStatus = userStatuses.find(status => abTestIdList.includes(status?.id));
    return matchingStatus?.id ?? null;
  }


  /**
   * Synchronizes the user's A/B test group assignments on authentication.
   * This method iterates through the list of A/B tests and ensures that the user is
   * assigned to the appropriate test group. If the user is not assigned to a group
   * and the test is configured to sync on authentication, the user is assigned to
   * a group. If the user is already assigned to a group, but the cookie value does
   * not match the user's group, the cookie is updated to the correct value.
   */
  public syncUserABTestGroupOnAuth() {
    Object.keys(AB_TEST_LIST).map(abTestTask => {
      const abTest = AB_TEST_LIST[abTestTask];
      const isComplete$ = new Subject<void>();
      abTest.resolvedValue$?.pipe(
        tap((group: ABVariantsType) => {
          const userGroup = this.checkExistUserGroups(abTest?.abTestVariants);
          if (abTest?.abTestVariants?.includes(group)) {
            if (!userGroup && abTest?.syncOnAuth) {
              this.assignUserToAbTestGroup(abTestTask, group);
              isComplete$.next();
              isComplete$.complete();
            }
            if (userGroup && abTest?.resolvedCookieValue !== userGroup) {
              this._cookie.delete(abTest?.resolvedCookieValue, '/');
              this._cookie.set(userGroup, userGroup, 999, '/');
              abTest.resolvedValue = userGroup as ABVariantsType;
              abTest.resolvedCookieValue = userGroup as ABVariantsType;
              abTest.resolvedValue$.next(userGroup as ABVariantsType);
              if (abTest?.debug) {
                createABTestDebugMessage(this._storage, abTestTask, `user already has group ${userGroup}, cookie ${group} moved to ${userGroup}`);
              }
            }
          }
        }),
        takeUntil(isComplete$),
      ).subscribe();
    });
  }

  /**
   * Handles the resolved value from the AB test request.
   * @param abTest - The AB test object.
   * @param abTestTask - The AB test task.
   * @param abTestValue - The resolved value from the AB test request.
   */
  private _handleResolvedValue(abTest: any, abTestTask: ABTestTaskListType, abTestValue: ABVariantsType) {
    if (abTestValue) {
      this._cookie.set(abTestValue, abTestValue, 999, '/');
      abTest.resolvedValue = abTestValue;
      abTest.resolvedCookieValue = abTestValue;
      abTest.resolvedValue$.next(abTestValue);
      if (abTest.debug) {
        createABTestDebugMessage(this._storage, abTestTask, `value resolved from http ${abTestValue}`);
      }
      this._rewriteForAuthUserCookies(abTest, abTestTask);
    }
  }

  /**
   * Rewrites the A/B test cookies for an authenticated user.
   *
   * This method checks the user's existing groups and updates the resolved A/B test
   * value and cookie accordingly. If debugging is enabled, a debug message is created.
   *
   * @param {object} abTest - The A/B test object.
   * @param {string} abTestTask - The A/B test task identifier.
   */
  private _rewriteForAuthUserCookies(abTest: any, abTestTask: ABTestTaskListType) {
    if (this._user?.info?.statuses) {
      const userGroup = this.checkExistUserGroups(abTest?.abTestVariants);
      const cookieGroup = abTest.resolvedValue;
      if (userGroup && abTest?.resolvedCookieValue !== userGroup) {
        this._cookie.delete(abTest?.resolvedCookieValue, '/');
        this._cookie.set(userGroup, userGroup, 999, '/');
        abTest.resolvedValue = userGroup as ABVariantsType;
        abTest.resolvedCookieValue = userGroup as ABVariantsType;
        abTest.resolvedValue$.next(userGroup as ABVariantsType);
        if (abTest?.debug) {
          createABTestDebugMessage(this._storage, abTestTask, `user already has group ${userGroup}, cookie ${cookieGroup} moved to ${userGroup}`);
        }
      }
    }
  }

  /**
   * Assigns a user to a specified A/B test group.
   *
   * This method sets the A/B test group for the given task and user,
   * adds the user to the specified A/B test group, and updates the resolved
   * value of the A/B test. If debugging is enabled, a debug message is created.
   *
   * @param {string} abTestTask - The A/B test task identifier.
   * @param {object} abTest - The A/B test object.
   * @param {string} group - The group identifier to assign the user to.
   */
  public assignUserToAbTestGroup(abTestTask, group) {
    const abTest = AB_TEST_LIST[abTestTask];
    this._addUserToABTestGroup(group);
    abTest.resolvedValue = group;
    if (abTest?.debug) {
      createABTestDebugMessage(this._storage, abTestTask, `added ab test group ${group} to user`);
    }
  }
}
